import React, { useState, useEffect } from "react";
import { Storage } from "aws-amplify";
import moment from "moment";
import { dateFormat } from "../../Tasks/configs/designTokens";
import MediaFetcher from "../library/MediaFetcher";
import DownloadAdUnitButton from "./DownloadAdUnitButton";
import DeleteAdUnitButton from "./DeleteAdUnitButton";
import { fileSizeInKB, fileSizeInMB } from "../utils";

const MetricBlock = ({ label, value, large, muted }) => (
  <div className="creative-metric-block">
    <span className="creative-metric-block__label">{label}</span>
    <span
      className={`creative-metric-block__value${
        large ? " creative-metric-block__value--large" : ""
      }${muted ? " creative-metric-block__value--muted" : ""}`}
    >
      {value}
    </span>
  </div>
);

const AdUnitHeroCard = ({
  orgMedia,
  isVideo = false,
  onDelete,
  assignedGroupCount = 0,
}) => {
  const [duration, setDuration] = useState(null);

  const hasValidMedia = orgMedia && orgMedia.media;
  const mediaKey = hasValidMedia ? orgMedia.media.key : null;

  // Detect video duration
  useEffect(() => {
    let isMounted = true;

    const detectVideoDuration = async () => {
      if (!isVideo || !hasValidMedia || !mediaKey) return;

      try {
        const videoUrl = await Storage.get(mediaKey, { expires: 600000 });
        const videoDuration = await new Promise((resolve) => {
          const video = document.createElement("video");
          video.preload = "metadata";

          const cleanup = () => {
            video.onloadedmetadata = null;
            video.onerror = null;
            video.src = "";
            video.load();
          };

          video.onloadedmetadata = () => {
            const dur = Math.round(video.duration);
            cleanup();
            resolve(dur);
          };

          video.onerror = () => {
            cleanup();
            resolve(null);
          };

          video.src = videoUrl;
        });

        if (isMounted && videoDuration) {
          setDuration(videoDuration);
        }
      } catch (error) {
        console.warn("Failed to detect video duration:", error);
      }
    };

    detectVideoDuration();

    return () => {
      isMounted = false;
    };
  }, [isVideo, hasValidMedia, mediaKey]);

  const formatDuration = (seconds) => {
    if (!seconds) return "--";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  if (!hasValidMedia) return null;

  const { media } = orgMedia;
  const fileSizeFormatter = isVideo ? fileSizeInMB : fileSizeInKB;
  const fileSizeUnit = isVideo ? "MB" : "KB";
  const createdAt = media.createdAt
    ? moment(media.createdAt).format(dateFormat.full)
    : "\u2014";

  return (
    <div className="creative-hero-card">
      {/* Action bar */}
      <div className="creative-hero-card__action-bar">
        <DownloadAdUnitButton
          creativeKeys={[media.key]}
          label="Download"
        />
        <DeleteAdUnitButton
          onDelete={() => onDelete([media.id])}
        />
      </div>

      {/* Grid body: preview | metrics */}
      <div className="creative-hero-card__body">
        {/* Dark preview area */}
        <div className="creative-hero-card__preview">
          <MediaFetcher
            mediaKey={media.key}
            className="creative-hero-card__media"
          />
        </div>

        {/* Metrics panel */}
        <div className="creative-hero-card__metrics">
          <div className="creative-hero-card__metrics-grid">
            <MetricBlock
              label="Aspect"
              value={`${media.width}x${media.height}`}
              large
            />
            <MetricBlock
              label="File Size"
              value={`${fileSizeFormatter(media.size)} ${fileSizeUnit}`}
            />
            <MetricBlock
              label="File Type"
              value={media.fileType}
            />
            {isVideo && (
              <MetricBlock
                label="Duration"
                value={formatDuration(duration)}
              />
            )}
            <MetricBlock
              label="Assigned Groups"
              value={assignedGroupCount}
            />
            <MetricBlock
              label="Created On"
              value={createdAt}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdUnitHeroCard;
